import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
export function About() {
  const images = ['https://cdn.magicpatterns.com/uploads/gqTsFFXRyNA5CdYKucUo6M/bluesky13.png', 'https://cdn.magicpatterns.com/uploads/nJ3V3u37ivFyjKtcE5Ji3r/bluesky2.png', 'https://cdn.magicpatterns.com/uploads/bRkP8eXuR8JEzMgdYbbx3o/bluesky3.png', 'https://cdn.magicpatterns.com/uploads/wTGSZgDYrwzN3d7aULTN4K/bluesky4.png', 'https://cdn.magicpatterns.com/uploads/1JeCAkJLAN77uqEagGtgfP/bluesky14.png', 'https://cdn.magicpatterns.com/uploads/bVNrfDwcHmoms2EkDfRtdQ/bluesky5.png'];
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);
  const goToNext = () => {
    setCurrentIndex(prev => (prev + 1) % images.length);
  };
  const goToPrevious = () => {
    setCurrentIndex(prev => (prev - 1 + images.length) % images.length);
  };
  return <section id="about" className="py-32 bg-cream relative overflow-hidden" data-id="element-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-15">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-16">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-17">
            <div className="flex items-center gap-4 mb-8" data-id="element-18">
              <div className="h-px w-16 bg-gold" data-id="element-19"></div>
              <span className="text-gold font-bold uppercase tracking-widest text-xs" data-id="element-20">
                Welcome to Blue Sky
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-navy mb-10 leading-[1.1]" data-id="element-21">
              We don't just renovate properties. <br data-id="element-22" />
              <span className="text-gold italic font-light" data-id="element-23">
                We transform houses into homes.
              </span>
            </h2>

            <div className="space-y-6 text-lg text-muted font-light leading-relaxed" data-id="element-24">
              <p data-id="element-25">
                Imagine waking up tomorrow morning... You walk into your kitchen
                — and it's exactly what you always dreamed it could be.
                Beautiful countertops. Custom cabinets. A space that makes you
                want to cook, gather, and celebrate life.
              </p>
              <p data-id="element-26">
                You step outside and look at your home's exterior. Fresh, clean,
                stunning. Your neighbors slow down as they walk by. Your guests
                arrive and their eyes go wide.
              </p>
              <div className="relative py-6 my-10" data-id="element-27">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gold" data-id="element-28"></div>
                <p className="font-heading text-2xl text-navy pl-8 italic" data-id="element-29">
                  "What does this homeowner truly want their home to feel like?"
                </p>
              </div>
              <p data-id="element-30">
                That's the question we ask at the start of every project.
                Because at the end of the day, your home is more than walls and
                a roof. It's where your family grows. Where memories are made.
                Where your hard work shows up in real, tangible, beautiful ways.
              </p>
              <p className="font-medium text-navy text-xl pt-4" data-id="element-31">
                And it deserves to look like it.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative" data-id="element-32">
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] border border-white/50 group" data-id="element-33">
              <div className="relative aspect-square" data-id="element-34">
                <AnimatePresence mode="wait" data-id="element-35">
                  <motion.img key={currentIndex} src={images[currentIndex]} alt={`Blue Sky project ${currentIndex + 1}`} className="w-full h-full object-cover" initial={{
                  opacity: 0,
                  scale: 1.05
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} transition={{
                  duration: 0.7,
                  ease: [0.22, 1, 0.36, 1]
                }} data-id="element-36" />
                </AnimatePresence>
                <div className="absolute inset-0 bg-navy/5 mix-blend-multiply" data-id="element-37"></div>

                {/* Navigation arrows */}
                <button onClick={goToPrevious} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-navy p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-label="Previous image" data-id="element-38">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-39" />
                </button>
                <button onClick={goToNext} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-navy p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-label="Next image" data-id="element-40">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-41" />
                </button>

                {/* Dots indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2" data-id="element-42">
                  {images.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-gold w-8' : 'bg-white/60 hover:bg-white/80'}`} aria-label={`Go to image ${index + 1}`} data-id="element-43" />)}
                </div>
              </div>
            </div>

            {/* Refined Floating badge */}
            <div className="absolute -bottom-10 -left-10 bg-white p-8 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] border border-gray-100 max-w-xs hidden md:block" data-id="element-44">
              <div className="flex items-center gap-5 mb-4" data-id="element-45">
                <div className="bg-cream w-14 h-14 rounded-full flex items-center justify-center text-gold" data-id="element-46">
                  <Heart size={24} data-id="element-47" />
                </div>
                <h3 className="font-heading font-bold text-2xl text-navy" data-id="element-48">
                  Built with Pride
                </h3>
              </div>
              <p className="text-sm text-muted leading-relaxed" data-id="element-49">
                Every detail matters when it's your family's home. We treat your
                property with the utmost respect.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}